import { useInfiniteQuery } from '@tanstack/react-query';
import { format } from 'date-fns';

import { Feedback } from '@bonusly/common-feedback';
import { useHttpClient } from '@bonusly/http-client';

import { Bonus } from '../../../types/bonus';
import { QueryKey } from '../types/growth-query-key';

// Raw item from the API before transformation
export interface RecentActivityRawItem {
	id: string;
	type: 'bonus' | 'feedback';
	created_at: string;
	data: any; // Raw bonus or feedback API data
}

// Transformed item after processing
export interface RecentActivityItem {
	id: string;
	type: 'bonus' | 'feedback';
	createdAt: string;
	data: Bonus | Feedback;
}

interface RecentActivityResponse {
	items: RecentActivityRawItem[];
	metadata: {
		total: number;
		page: number;
		per_page: number;
		total_pages: number;
		next_page: number | null;
	};
}

interface RecentActivityQueryParams {
	targetUserId: string;
	viewerId: string;
	activityType?: 'received' | 'given';
}

/**
 * Hook to fetch recent activity (bonuses and feedback) for a user
 * This hook will call a new backend endpoint: /one_on_ones/recent_activity
 * that returns paginated activity sorted by date (newest first)
 *
 * @param targetUserId - The user whose activity you want to retrieve
 * @param viewerId - The user who is viewing the activity (for permissions/context)
 * @param activityType - Type of activity: 'received' or 'given'
 */
export const useRecentActivity = ({ targetUserId, viewerId, activityType = 'received' }: RecentActivityQueryParams) => {
	const http = useHttpClient('v2');

	const endDate = format(new Date(), 'yyyy-MM-dd'); // Always use the current date

	return useInfiniteQuery(
		[QueryKey.RecentActivity, targetUserId, viewerId, endDate, activityType],
		async ({ pageParam = 1 }) => {
			const response = await http.get<RecentActivityResponse>('/one_on_ones/recent_activity', {
				params: {
					target_user_id: targetUserId,
					viewer_id: viewerId,
					page: pageParam,
					per_page: 10,
					end_date: endDate,
					activity_type: activityType,
				},
			});

			const items = response.data?.items || [];
			// Apply transformations to the raw data, handling the API structure
			const transformedItems = items
				.map((rawItem) => {
					try {
						const type = rawItem.type;
						const data = rawItem.data;

						let transformedItem: Bonus | Feedback;

						// Transform based on item type
						if (type === 'feedback') {
							try {
								transformedItem = Feedback.fromApiData(data);

								// Filter feedback to only include those shared with the viewer user
								// Only apply sharing filter if viewer is different from target (e.g., manager viewing employee)
								// Users viewing their own feedback should see all of it
								if (viewerId && viewerId !== targetUserId && !transformedItem.sharedWithUserIds?.includes(viewerId)) {
									return null; // Skip items not shared with viewer
								}
							} catch (error) {
								return null;
							}
						} else if (type === 'bonus') {
							// For bonuses, use the data directly
							transformedItem = data as Bonus;
						} else {
							return null;
						}

						return {
							id: rawItem.id,
							type,
							createdAt: rawItem.created_at,
							data: transformedItem,
						} as RecentActivityItem;
					} catch (error) {
						return null;
					}
				})
				.filter(Boolean) as RecentActivityItem[]; // Remove null items

			return {
				items: transformedItems,
				page: response.data.metadata.page,
				nextPage: response.data.metadata.next_page,
				hasNextPage: !!response.data.metadata.next_page,
			};
		},
		{
			enabled: !!targetUserId && !!viewerId,
			getNextPageParam: (lastPage) => lastPage.nextPage,
			staleTime: 1000 * 30, // 30 seconds
		}
	);
};
